import { PVC_SIGN_ROUTE_PATH } from "../../../../packages/pvc-product/pvc-sign-route";
import {
  PRODUCT_PAGE_PREFIX,
  productPagePath,
} from "../product-pages/product-pages-content";

export type SearchResult = {
  id: string;
  label: string;
  category: string;
  href: string;
};

type SearchEntry = SearchResult & { keywords: string[] };

/**
 * A small curated local search index built from the same homepage product
 * content. No dedicated catalog/search Shop API query exists yet, so this
 * stays local rather than introducing a new backend endpoint.
 */
const SEARCH_INDEX: SearchEntry[] = [
  {
    id: "search-pvc-sign",
    label: "שלט PVC לפי מידה",
    category: "שילוט",
    href: PVC_SIGN_ROUTE_PATH,
    keywords: ["שלט", "שלטים", "pvc", "פי וי סי", "שילוט", "לוחית"],
  },
  {
    id: "search-rollup",
    label: "רולאפים ותצוגה",
    category: "תצוגה",
    href: productPagePath("rollup"),
    keywords: ["רולאפ", "רול אפ", "רולאפים", "תצוגה", "עמדת תצוגה"],
  },
  {
    id: "search-foam-boards",
    label: "קאפה וקירות צילום",
    category: "אירועים ותצוגה",
    href: productPagePath("foam-boards"),
    keywords: ["קאפה", "קיר צילום", "קירות צילום", "פוטו וול", "לוח קאפה"],
  },
  {
    id: "search-banners",
    label: "שמשוניות",
    category: "שילוט בפורמט גדול",
    href: productPagePath("banners"),
    keywords: ["שמשונית", "שמשוניות", "באנר", "שלט חוץ", "פורמט גדול"],
  },
  {
    id: "search-stickers",
    label: "מדבקות",
    category: "מדבקות",
    href: productPagePath("stickers-roll"),
    keywords: ["מדבקה", "מדבקות", "sticker", "גליל מדבקות"],
  },
  {
    id: "search-events",
    label: "אירועים ושמחות",
    category: "אירועים",
    href: productPagePath("event-invitations"),
    keywords: ["אירוע", "אירועים", "שמחה", "שמחות", "חתונה", "בר מצווה", "בת מצווה", "חינה", "יום הולדת"],
  },
  {
    id: "search-business-print",
    label: "דפוס עסקי",
    category: "עסקים",
    href: productPagePath("business-cards"),
    keywords: ["כרטיס ביקור", "כרטיסי ביקור", "פלייר", "פליירים", "דפוס עסקי", "משרד"],
  },
  {
    id: "search-gifts",
    label: "מתנות ממותגות",
    category: "מיתוג",
    href: productPagePath("printed-tshirts"),
    keywords: ["מיתוג", "מתנה", "מתנות", "לוגו", "חולצה", "חולצות"],
  },
];

/** Trim, collapse internal whitespace and case-fold. Hebrew has no case, but
 * queries may still mix in Latin letters (e.g. "PVC"). */
export function normalizeSearchQuery(raw: string): string {
  return raw.trim().replace(/\s+/g, " ").toLowerCase();
}

export function searchHomepageIndex(
  rawQuery: string,
  limit = 8,
  publicProductSlugs: ReadonlySet<string> | null = null,
): SearchResult[] {
  const query = normalizeSearchQuery(rawQuery);
  if (!query) return [];

  const matches = SEARCH_INDEX.filter(entry => {
    if (publicProductSlugs && entry.href.startsWith(PRODUCT_PAGE_PREFIX)) {
      const slug = entry.href.slice(PRODUCT_PAGE_PREFIX.length);
      if (!publicProductSlugs.has(slug)) return false;
    }
    return (
      entry.label.toLowerCase().includes(query) ||
      entry.keywords.some(keyword => keyword.toLowerCase().includes(query))
    );
  });

  return matches.slice(0, limit).map(({ keywords: _keywords, ...result }) => result);
}

/** Keeps a keyboard-navigated active index inside [-1, length-1] no matter
 * which direction or how far past the edge the caller moves it. -1 means
 * "no result highlighted". */
export function clampActiveResultIndex(nextIndex: number, resultCount: number): number {
  if (resultCount <= 0) return -1;
  if (nextIndex < -1) return resultCount - 1;
  if (nextIndex >= resultCount) return 0;
  return nextIndex;
}
